"use client";

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import Flex from "../../Flex";
import { getColor } from "../../utils";
import {
  EditingContext,
  EditingCell,
  CellPositionContext,
  CellKey,
  CellRegistration,
  makeCellKey,
  parseCellKey,
} from "./EditableTableContext";
import { coerceValueForPaste } from "./coerceValueForPaste";
import TableHeader, { TableColumn } from "../TableHeader";
import { useColumnResize } from "../useColumnResize";
import BulkCellContent, {
  BulkRowWrapper,
  BulkEditInfo,
  BULK_EDIT_BAR_HEIGHT,
} from "./BulkCellContent";
import EditableTableRow, { ROW_HANDLE_WIDTH, EditableCellWrapper } from "./EditableTableRow";
import { useTableSelection } from "./useTableSelection";
import { useTableKeyboard } from "./useTableKeyboard";

/** Generic column type for EditableTable — extends TableColumn with a typed renderer. */
export interface EditableTableColumn<DataType = unknown> extends TableColumn {
  renderer?: (data: DataType) => React.ReactNode;
  /** For non-EditableTableCell columns: return a string value for copy support. */
  copyValue?: (data: DataType) => string;
  /** Custom paste handler for columns that don't use EditableTableCell (e.g. images). */
  onPaste?: (rowId: string, text: string) => void;
  /** Custom clear handler for columns that don't use EditableTableCell (e.g. images). Called on Delete/Backspace. */
  onClear?: (rowId: string) => void;
  /** Prevent this column's cells from being selected (e.g. image/preview columns). */
  disableSelection?: boolean;
  /** Background color token applied to cells in this column (e.g. "grayLight"). */
  background?: string;
  /** If true, the cell in the pinned top row renders blank/white with no content. */
  pinnedRowHidden?: boolean;
}

type Props<DataType> = {
  data: DataType[];
  columns: EditableTableColumn<DataType>[];
  getRowId: (row: DataType) => string;
  rowHeight?: number;
  sort?: string;
  onSort?: (key: string) => void;
  onRowClick?: (rowId: string) => void;
  /** Optional row pinned above the column headers. Renders with the same column widths but is not part of the sortable data. */
  pinnedTopRow?: { data: DataType };
  /** Returns menu actions for a given row. If undefined or empty, no overflow button is shown. */
  getRowActions?: (rowId: string) => Array<{ title: string; onClick: () => void }> | undefined;
  /** localStorage key for persisting user-resized column widths across sessions. */
  columnWidthsStorageKey?: string;
};

const PINNED_ROW_HEIGHT = 40,
  EDITABLE_TABLE_FONT_SIZE = 13;

const PinnedRowWrapper = styled(Flex).attrs({
  alignItems: "center",
  borderBottom: "default",
})<{ $top: number }>`
  position: sticky;
  top: ${p => p.$top}px;
  z-index: 65;
  height: ${PINNED_ROW_HEIGHT}px;
  min-height: ${PINNED_ROW_HEIGHT}px;
  flex-shrink: 0;
  background: white;
`;

const PinnedRowLabel = styled.div`
  width: ${ROW_HANDLE_WIDTH}px;
  padding-left: 8px;
  font-size: 10px;
  font-weight: 600;
  color: ${getColor("grayDark")};
  user-select: none;
`;

const EditableTable = <DataType,>({
  data,
  columns,
  getRowId,
  rowHeight = 80,
  sort,
  onSort,
  onRowClick,
  pinnedTopRow,
  getRowActions,
  columnWidthsStorageKey,
}: Props<DataType>) => {
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editingInitialChar, setEditingInitialChar] = useState<string | null>(null);
  const [openRowMenuId, setOpenRowMenuId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const cellRegistryRef = useRef<Map<CellKey, CellRegistration>>(new Map());

  const { widths, getResizeHandleProps } = useColumnResize(columns, columnWidthsStorageKey);
  // Keep a ref so scrollColIntoView always reads current widths without being recreated on resize.
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingInitialChar(null);
    tableRef.current?.focus({ preventScroll: true });
  }, []);

  // --- Selection ---

  const {
    selectedCells,
    anchorCellRef,
    selectCell,
    clearSelection,
    isCellSelected,
    expandSelection,
    selectColumn,
    advanceCell,
    tabCell,
    anchorToCell,
  } = useTableSelection({
    data,
    getRowId,
    columns,
    tableRef: tableRef as React.RefObject<HTMLDivElement | null>,
    widthsRef,
    pinnedTopRow,
  });

  // --- Editing ---

  // Use a ref so startEdit is always stable (no deps) while always calling the latest anchorToCell.
  const anchorToCellRef = useRef(anchorToCell);
  anchorToCellRef.current = anchorToCell;

  const startEdit = useCallback((rowId: string, colIndex: number, initialChar?: string) => {
    setEditingCell({ rowId, colIndex });
    setEditingInitialChar(initialChar ?? null);
    anchorToCellRef.current(rowId, colIndex);
  }, []);

  // --- Keyboard / Clipboard ---

  const { handleKeyDown, handleCopyEvent, handlePaste } = useTableKeyboard({
    selectedCells,
    anchorCellRef,
    clearSelection,
    expandSelection,
    advanceCell,
    tabCell,
    editingCell,
    startEdit,
    cancelEdit,
    columns,
    data,
    getRowId,
    cellRegistryRef,
    pinnedTopRow,
  });

  // Refocus the table container when edit ends so keyboard shortcuts work
  useEffect(() => {
    if (!editingCell && tableRef.current) {
      tableRef.current.focus({ preventScroll: true });
    }
  }, [editingCell]);

  // --- Click outside to clear selection ---

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // If the target is no longer in the document, a React re-render removed it during
      // the same click event (e.g. a cell transitioning from display-text to an input).
      // Treat detached nodes as "inside" so we don't incorrectly clear the new selection.
      if (!document.contains(target)) return;
      if (tableRef.current && !tableRef.current.contains(target)) {
        clearSelection();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [clearSelection]);

  // --- Cell Registry (ref-based, no re-renders) ---

  const registerCell = useCallback((rowId: string, colIndex: number, reg: CellRegistration) => {
    cellRegistryRef.current.set(makeCellKey(rowId, colIndex), reg);
  }, []);

  const unregisterCell = useCallback((rowId: string, colIndex: number) => {
    cellRegistryRef.current.delete(makeCellKey(rowId, colIndex));
  }, []);

  const getCellRegistration = useCallback((key: CellKey) => {
    return cellRegistryRef.current.get(key);
  }, []);

  // --- Bulk Edit ---

  const commitBulkForColumn = useCallback(
    (colIndex: number, value: string | number | boolean) => {
      for (const key of selectedCells) {
        const { colIndex: c } = parseCellKey(key);
        if (c !== colIndex) continue;
        const reg = cellRegistryRef.current.get(key);
        if (!reg) continue;
        const coerced = coerceValueForPaste(
          String(value),
          reg.type,
          reg.options,
          reg.currencySymbol
        );
        if (coerced !== null) reg.onCommit(coerced);
        else if (typeof value === "boolean") reg.onCommit(value);
      }
      tableRef.current?.focus({ preventScroll: true });
    },
    [selectedCells]
  );

  // Show bulk edit cells for each column where 2+ cells are selected
  const bulkEditInfos = useMemo((): BulkEditInfo[] => {
    if (selectedCells.size < 2) return [];
    const byCol = new Map<number, CellKey[]>();
    for (const key of selectedCells) {
      const { colIndex: c } = parseCellKey(key);
      if (!byCol.has(c)) byCol.set(c, []);
      byCol.get(c)!.push(key);
    }
    const result: BulkEditInfo[] = [];
    for (const [colIndex, keys] of byCol) {
      if (keys.length < 2) continue;
      const col = columns[colIndex] as EditableTableColumn<DataType> | undefined;
      if (!col || col.disableSelection) continue;
      const reg = cellRegistryRef.current.get(keys[0]);
      if (!reg) continue;
      const offsetLeft =
        widths.slice(0, colIndex).reduce((acc, w) => acc + w, 0) + ROW_HANDLE_WIDTH;
      const initialChecked =
        reg.type === "boolean"
          ? keys.some(k => {
              const v = cellRegistryRef.current.get(k)?.value;
              return v === true || v === "true";
            })
          : false;
      result.push({
        colIndex,
        type: reg.type,
        options: reg.options,
        offsetLeft,
        width: widths[colIndex],
        initialChecked,
        currencySymbol: reg.currencySymbol,
      });
    }
    result.sort((a, b) => a.colIndex - b.colIndex);
    return result;
  }, [selectedCells, columns, widths]);

  // --- Context ---

  const contextValue = useMemo(
    () => ({
      editingCell,
      editingInitialChar,
      startEdit,
      cancelEdit,
      advanceCell,
      tabCell,
      selectedCells,
      selectCell,
      clearSelection,
      isCellSelected,
      registerCell,
      unregisterCell,
      getCellRegistration,
    }),
    [
      editingCell,
      editingInitialChar,
      startEdit,
      cancelEdit,
      advanceCell,
      tabCell,
      selectedCells,
      selectCell,
      clearSelection,
      isCellSelected,
      registerCell,
      unregisterCell,
      getCellRegistration,
    ]
  );

  const totalWidth = widths.reduce((acc, w) => acc + w, 0) + ROW_HANDLE_WIDTH;

  const bulkBarHeight = bulkEditInfos.length > 0 ? BULK_EDIT_BAR_HEIGHT : 0;
  const pinnedRowTop = bulkBarHeight;
  const headerTop = bulkBarHeight + (pinnedTopRow ? PINNED_ROW_HEIGHT : 0);

  return (
    <EditingContext.Provider value={contextValue}>
      <div
        ref={tableRef}
        style={{ minWidth: totalWidth, outline: "none", fontSize: EDITABLE_TABLE_FONT_SIZE }}
        tabIndex={0}
        role="grid"
        onKeyDown={handleKeyDown}
        onCopy={handleCopyEvent}
        onPaste={handlePaste}>
        {bulkEditInfos.length > 0 && (
          <BulkRowWrapper>
            {bulkEditInfos.map(info => (
              <BulkCellContent
                key={info.colIndex}
                info={info}
                onCommit={v => commitBulkForColumn(info.colIndex, v)}
              />
            ))}
          </BulkRowWrapper>
        )}
        {pinnedTopRow &&
          (() => {
            const pinnedRowId = getRowId(pinnedTopRow.data);
            return (
              <PinnedRowWrapper $top={pinnedRowTop}>
                <PinnedRowLabel>Defaults</PinnedRowLabel>
                {columns.map((col, colIdx) => (
                  <CellPositionContext.Provider
                    key={colIdx}
                    value={{ rowId: pinnedRowId, colIndex: colIdx }}>
                    <EditableCellWrapper
                      $selected={!col.disableSelection && isCellSelected(pinnedRowId, colIdx)}
                      $background={col.pinnedRowHidden ? undefined : col.background}
                      style={{ width: widths[colIdx] }}
                      onClick={
                        col.disableSelection
                          ? () => clearSelection()
                          : (e: React.MouseEvent) => {
                              if (
                                editingCell?.rowId === pinnedRowId &&
                                editingCell?.colIndex === colIdx
                              )
                                return;
                              selectCell(pinnedRowId, colIdx, e.metaKey || e.ctrlKey, e.shiftKey);
                            }
                      }>
                      {col.pinnedRowHidden ? null : col.renderer?.(pinnedTopRow.data)}
                    </EditableCellWrapper>
                  </CellPositionContext.Provider>
                ))}
              </PinnedRowWrapper>
            );
          })()}
        <TableHeader
          columns={columns}
          widths={widths}
          sort={sort}
          onSort={onSort}
          headerSpacer={ROW_HANDLE_WIDTH}
          getResizeHandleProps={getResizeHandleProps}
          onSelectColumn={selectColumn}
          top={`${headerTop}px`}
        />
        {data.map(row => {
          const rowId = getRowId(row);
          return (
            <EditableTableRow
              key={rowId}
              row={row}
              rowId={rowId}
              columns={columns}
              widths={widths}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              getRowActions={getRowActions}
              isMenuOpen={openRowMenuId === rowId}
              setOpenRowMenuId={setOpenRowMenuId}
            />
          );
        })}
      </div>
    </EditingContext.Provider>
  );
};

export default EditableTable;
